import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { Maximize2, Wifi, WifiOff, Radio, AlertCircle } from "lucide-react";

// ✅ HTTPS Stream Server
const STREAM_BASE_URL = "https://cognivision-be.citriot.ai/cameras";

const cameraList = [
  { id: "CAM-01", name: "Gate Outside", location: "Building Entrance", status: "online" },
  { id: "CAM-02", name: "Kitchen 1", location: "Kitchen Area", status: "online" },
  { id: "CAM-03", name: "Laundry Area", location: "Laundry Room", status: "online" },
  { id: "CAM-04", name: "Parking", location: "Parking Lot", status: "online" },
  { id: "CAM-05", name: "Lift Lobby", location: "Elevator Area", status: "online" },
];

// ✅ All cameras use HTTPS (MJPEG streams — served via multipart/x-mixed-replace)
const streamMap = {
  "CAM-01": `${STREAM_BASE_URL}/stream/gate_outside`,
  "CAM-02": `${STREAM_BASE_URL}/stream/kitchen_1`,
  "CAM-03": `${STREAM_BASE_URL}/stream/laundry_area`,
  "CAM-04": `${STREAM_BASE_URL}/stream/parking`,
  "CAM-05": `${STREAM_BASE_URL}/stream/lift_lobby`,
};

const SkeletonCard = () => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden animate-pulse">
    <div className="w-full bg-slate-800" style={{ aspectRatio: "16/9" }} />
    <div className="px-4 py-3 flex items-center justify-between">
      <div className="space-y-2">
        <div className="h-3 w-28 rounded bg-slate-700" />
        <div className="h-2.5 w-20 rounded bg-slate-800" />
      </div>
      <div className="h-3.5 w-3.5 rounded-full bg-slate-700" />
    </div>
  </div>
);

const StreamLoader = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950 z-20 gap-3">
    <div className="relative h-11 w-11">
      <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
      <div
        className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-500"
        style={{ animation: "spin 0.85s linear infinite" }}
      />
    </div>
    <p className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase">
      Connecting…
    </p>
  </div>
);

const StreamError = ({ message, onRetry }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center z-30 bg-slate-950/90 gap-3">
    <AlertCircle size={32} className="text-red-500" />
    <p className="text-sm text-slate-400">{message || "Stream unavailable"}</p>
    <p className="text-xs text-slate-600">Check connection or try again</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-2 px-4 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-xs font-semibold hover:bg-blue-500/30 transition-colors"
      >
        Retry
      </button>
    )}
  </div>
);

const CameraCard = ({ cam, onClick }) => {
  const isOnline = cam.status === "online";
  return (
    <button
      onClick={() => isOnline && onClick(cam.id)}
      disabled={!isOnline}
      className={[
        "group relative overflow-hidden rounded-2xl border bg-slate-900 text-left transition-all duration-300",
        isOnline
          ? "border-slate-700/60 hover:border-blue-500/70 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-0.5 cursor-pointer"
          : "border-slate-800 opacity-40 cursor-not-allowed",
      ].join(" ")}
    >
      <div className="relative w-full bg-slate-950" style={{ aspectRatio: "16/9" }}>
        <div className="absolute inset-0 flex items-center justify-center">
          {isOnline ? (
            <div className="flex flex-col items-center gap-2 select-none">
              <div className="relative">
                <Radio size={28} className="text-blue-500/60" />
                <div className="absolute inset-0 rounded-full bg-blue-500/20"
                  style={{ animation: "ping 2s cubic-bezier(0,0,0.2,1) infinite" }} />
              </div>
              <p className="text-[11px] font-medium text-slate-500 tracking-wide">Click to stream</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <AlertCircle size={24} className="text-slate-600" />
              <p className="text-[11px] text-slate-600">Offline</p>
            </div>
          )}
        </div>

        <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/50 to-transparent z-10" />
        <div className="absolute left-3 top-3 z-20">
          <span className="rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-bold tracking-widest text-slate-300 backdrop-blur-sm border border-white/5">
            {cam.id}
          </span>
        </div>

        {isOnline && (
          <div className="absolute right-3 top-3 z-20 flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
          </div>
        )}

        {isOnline && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 z-10">
            <div className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 backdrop-blur-md border border-white/15 shadow-lg">
              <Maximize2 size={13} className="text-white" />
              <span className="text-xs font-semibold text-white tracking-wide">Expand Feed</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/60">
        <div>
          <p className="text-sm font-bold text-slate-100 leading-tight">{cam.name}</p>
          <p className="mt-0.5 text-[11px] text-slate-500">{cam.location}</p>
        </div>
        {isOnline
          ? <Wifi size={14} className="text-emerald-500 shrink-0" />
          : <WifiOff size={14} className="text-slate-600 shrink-0" />}
      </div>
    </button>
  );
};

const TanCameraView = () => {
  const [modalCam, setModalCam] = useState(null);
  const [streamReady, setStreamReady] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [imgKey, setImgKey] = useState(0); // bump to force <img> to remount and reconnect

  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 900);
    return () => clearTimeout(t);
  }, []);

  const openCamera = (id) => {
    setStreamReady(false);
    setStreamError(false);
    setImgKey((k) => k + 1);
    setModalCam(id);
  };

  const closeCamera = () => {
    setModalCam(null);
    setStreamReady(false);
    setStreamError(false);
  };

  const retryStream = () => {
    setStreamReady(false);
    setStreamError(false);
    setImgKey((k) => k + 1); // new key -> React remounts <img> -> fresh MJPEG connection
  };

  const selectedCam = cameraList.find((c) => c.id === modalCam);
  const onlineCount = cameraList.filter((c) => c.status === "online").length;

  return (
    <>
      <div className="mb-7">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-500">
            Live Surveillance
          </p>
        </div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900">Camera Management</h1>
        <div className="mt-2 flex items-center gap-3">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{onlineCount}</span> of {cameraList.length} cameras online
          </p>
          <span className="h-1 w-1 rounded-full bg-slate-300" />
          <p className="text-sm text-slate-400">Click any feed to expand</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {pageLoading
          ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
          : cameraList.map((cam) => (
              <CameraCard key={cam.id} cam={cam} onClick={openCamera} />
            ))}
      </div>

      <Modal
        open={!!modalCam}
        onCancel={closeCamera}
        footer={null}
        width="min(860px, 92vw)"
        centered
        destroyOnClose
        styles={{
          content: {
            background: "#0c1323",
            borderRadius: 16,
            padding: 0,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.06)",
            boxShadow: "0 32px 72px rgba(0,0,0,0.6)",
          },
          mask: {
            backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.75)",
          },
        }}
        closeIcon={
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/8 text-slate-400 hover:bg-white/15 hover:text-white transition-all text-sm">
            ✕
          </span>
        }
      >
        {selectedCam && (
          <div>
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/5">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-blue-400 mb-0.5">
                  Live Feed · {selectedCam.id}
                </p>
                <h2 className="text-lg font-black text-white leading-tight">{selectedCam.name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{selectedCam.location}</p>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="text-xs font-bold text-emerald-400 tracking-widest">LIVE</span>
              </div>
            </div>

            <div className="px-4 pt-3 pb-4">
              <div
                className="relative overflow-hidden rounded-xl bg-black"
                style={{ aspectRatio: "16/9" }}
              >
                {!streamReady && !streamError && <StreamLoader />}
                {streamError && <StreamError message="Failed to load stream" onRetry={retryStream} />}

                {/* MJPEG streams (multipart/x-mixed-replace) render natively via <img>,
                    not <video> — browsers have no <video> decoder for this format. */}
                <img
                  key={imgKey}
                  src={streamMap[modalCam]}
                  alt={`${selectedCam.name} live feed`}
                  className="w-full h-full object-contain"
                  onLoad={() => {
                    setStreamReady(true);
                    setStreamError(false);
                  }}
                  onError={() => {
                    setStreamReady(true);
                    setStreamError(true);
                  }}
                  style={{ display: streamReady && !streamError ? "block" : "none" }}
                />
              </div>
            </div>

            <div className="border-t border-white/5 px-5 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-slate-600">{modalCam}</span>
                <span className="h-1 w-1 rounded-full bg-slate-700" />
                <span className="text-xs text-slate-600">MJPEG Stream</span>
              </div>
              <span className="text-xs text-slate-600">
                {new Date().toLocaleDateString(undefined, {
                  weekday: "short", year: "numeric", month: "short", day: "numeric",
                })}
              </span>
            </div>
          </div>
        )}
      </Modal>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }
      `}</style>
    </>
  );
};

export default TanCameraView;